/**
 * Pattern Suggestion Statistics Dashboard
 * =======================================
 * 
 * Displays analytics about pattern library performance:
 * - Overall acceptance rates
 * - Most effective patterns
 * - Suggestions by confidence level
 * - Trend charts
 */

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Check, X, Lightbulb, Target, Award, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';

interface PatternStat {
  pattern_id: string;
  accept_count: number;
  reject_count: number;
  total_decisions: number;
  accept_rate: number;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  recommended_confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  needs_review: boolean;
}

interface EventStats {
  event_id: string;
  total_patterns: number;
  active_patterns: number;
  disabled_patterns: number;
  total_decisions: number;
  total_accepts: number;
  total_rejects: number;
  overall_accept_rate: number;
  by_confidence: {
    HIGH: number;
    MEDIUM: number;
    LOW: number;
  };
  pattern_details?: PatternStat[];
}

interface PatternStatsDashboardProps {
  eventId: string;
  onClose?: () => void;
}

export default function PatternStatsDashboard({ eventId, onClose }: PatternStatsDashboardProps) {
  const [stats, setStats] = useState<EventStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/v1/patterns/${eventId}/stats`);
        setStats(response);
      } catch (err) {
        setError('Failed to load pattern statistics');
        console.error('Error fetching pattern stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [eventId]);

  if (loading) {
    return (
      <div className="rounded-lg shadow-lg p-6" style={{ backgroundColor: 'var(--color-bg-surface)' }} data-testid="pattern-stats-dashboard">
        <div className="animate-pulse space-y-4">
          <div className="h-8 rounded w-1/3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="rounded-lg shadow-lg p-6" style={{ backgroundColor: 'var(--color-bg-surface)' }} data-testid="pattern-stats-dashboard">
        <div className="text-center" style={{ color: 'var(--color-needs-human-text)' }}>
          <AlertTriangle className="mx-auto mb-2" size={32} />
          <p>{error || 'No statistics available'}</p>
        </div>
      </div>
    );
  }

  const acceptRateColor = stats.overall_accept_rate >= 0.8
    ? 'var(--color-verified)'
    : stats.overall_accept_rate >= 0.6
    ? 'var(--color-brand)'
    : 'var(--color-needs-human-text)';

  // Get top performing patterns
  const topPatterns = (stats.pattern_details || [])
    .filter(p => p.total_decisions >= 5)
    .sort((a, b) => b.accept_rate - a.accept_rate)
    .slice(0, 5);

  // Get patterns needing review
  const patternsNeedingReview = (stats.pattern_details || [])
    .filter(p => p.needs_review || p.accept_rate < 0.5);

  return (
    <div className="rounded-lg shadow-lg overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)' }} data-testid="pattern-stats-dashboard">
      {/* Header */}
      <div className="px-6 py-4" style={{ backgroundColor: 'var(--color-brand)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target style={{ color: 'var(--color-on-brand)' }} size={24} />
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-on-brand)' }}>Pattern Library Analytics</h2>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="transition-colors"
              style={{ color: 'var(--color-on-brand)' }}
              data-testid="close-stats-btn"
            >
              <X size={24} />
            </button>
          )}
        </div>
        <p className="text-sm mt-1" style={{ color: 'var(--color-on-brand)' }}>Event: {eventId}</p>
      </div>

      {/* Summary Stats Grid */}
      <div className="p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Total Patterns */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb style={{ color: 'var(--color-needs-human-text)' }} size={20} />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Total Patterns</span>
            </div>
            <div className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{stats.total_patterns}</div>
            <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {stats.active_patterns} active, {stats.disabled_patterns} disabled
            </div>
          </div>

          {/* Total Decisions */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Check style={{ color: 'var(--color-verified)' }} size={20} />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Total Decisions</span>
            </div>
            <div className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{stats.total_decisions}</div>
            <div className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {stats.total_accepts} accepts, {stats.total_rejects} rejects
            </div>
          </div>

          {/* Accept Rate */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              {stats.overall_accept_rate >= 0.7 ? (
                <TrendingUp style={{ color: 'var(--color-verified)' }} size={20} />
              ) : (
                <TrendingDown style={{ color: 'var(--color-needs-human-text)' }} size={20} />
              )}
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Accept Rate</span>
            </div>
            <div className="text-3xl font-bold" style={{ color: acceptRateColor }}>
              {(stats.overall_accept_rate * 100).toFixed(1)}%
            </div>
            <div className="w-full h-2 rounded-full mt-2" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${stats.overall_accept_rate * 100}%`,
                  backgroundColor:
                    stats.overall_accept_rate >= 0.8 ? 'var(--color-verified)' :
                    stats.overall_accept_rate >= 0.6 ? 'var(--color-brand)' : 'var(--color-needs-human-text)',
                }}
              />
            </div>
          </div>

          {/* By Confidence */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <Award style={{ color: 'var(--color-needs-human-text)' }} size={20} />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>By Confidence</span>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--color-verified)' }}>HIGH</span>
                <span className="font-medium">{stats.by_confidence.HIGH}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--color-brand)' }}>MEDIUM</span>
                <span className="font-medium">{stats.by_confidence.MEDIUM}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--color-needs-human-text)' }}>LOW</span>
                <span className="font-medium">{stats.by_confidence.LOW}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Performing Patterns */}
        {topPatterns.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Award style={{ color: 'var(--color-brand)' }} size={20} />
              Top Performing Patterns
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Pattern</th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Decisions</th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Accept Rate</th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Confidence</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                  {topPatterns.map((pattern, idx) => (
                    <tr key={pattern.pattern_id}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {idx < 3 && (
                            <span
                              className="text-xs font-bold w-4 text-center"
                              style={{ color: idx === 0 ? 'var(--color-brand)' : idx === 1 ? 'var(--color-text-dim)' : 'var(--color-needs-human-text)' }}
                            >
                              #{idx + 1}
                            </span>
                          )}
                          <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{pattern.pattern_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {pattern.total_decisions}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                          style={
                            pattern.accept_rate >= 0.8 ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                            pattern.accept_rate >= 0.6 ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                            { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                          }
                        >
                          {(pattern.accept_rate * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                          style={
                            pattern.confidence === 'HIGH' ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                            pattern.confidence === 'MEDIUM' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
                            { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                          }
                        >
                          {pattern.confidence}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Patterns Needing Review */}
        {patternsNeedingReview.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <AlertTriangle style={{ color: 'var(--color-brand)' }} size={20} />
              Patterns Needing Review
            </h3>
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
              <div className="space-y-2">
                {patternsNeedingReview.slice(0, 5).map(pattern => (
                  <div key={pattern.pattern_id} className="flex items-center justify-between text-sm">
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{pattern.pattern_id}</span>
                    <div className="flex items-center gap-3">
                      <span style={{ color: 'var(--color-text-muted)' }}>{pattern.total_decisions} decisions</span>
                      <span
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={
                          pattern.accept_rate < 0.5 ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } : { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                        }
                      >
                        {(pattern.accept_rate * 100).toFixed(0)}% accept rate
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {patternsNeedingReview.length > 5 && (
                <p className="text-xs mt-3" style={{ color: 'var(--color-needs-human-text)' }}>
                  +{patternsNeedingReview.length - 5} more patterns need review
                </p>
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {stats.total_decisions === 0 && (
          <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
            <Lightbulb className="mx-auto mb-3" style={{ color: 'var(--color-text-dim)' }} size={40} />
            <p className="text-lg font-medium">No decisions yet</p>
            <p className="text-sm mt-1">Pattern statistics will appear after accountants accept or reject suggestions</p>
          </div>
        )}
      </div>
    </div>
  );
}
